import { TextProps as RNTextProps, Text as RNText, TextStyle, Platform } from "react-native"
import { functionalColors } from "./colors"
import { useTheme } from "@emotion/react"

export type TextTypes =
  | "h0"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "p"
  | "label"
  | "label2"
  | "label3"
  | "sectionTitle"
  | "primaryButton"
  | "primaryButton3D"
  | "secondaryButton"
  | "secondaryButton3D"
  | "body1"
  | "body2"
  | "body3"
  | "unnamed"

export type TextProps = {
  type?: TextTypes
  color?: keyof (typeof functionalColors)["text"]
  fontFamily?:
    | "Tobias Light"
    | "IBMPlexSans-Bold"
    | "IBMPlexSans-Light"
    | "IBMPlexSans-Medium"
    | "IBMPlexSans-Regular"
    | "IBMPlexSans-SemiBold"
    | "IBMPlexSans-Thin"
    | "FFFAcidGrotesk-Book"
    | "FFFAcidGrotesk-Medium"
  underline?: boolean
  align?: TextStyle["textAlign"]
  fontSize?: number
  lineHeight?: number
} & RNTextProps

export function useTextStyles() {
  const styles: { [textType in TextTypes]: TextStyle } = {
    h0: {
      fontFamily: "IBMPlexSans-Bold",
      fontSize: 48,
      lineHeight: 57,
    },
    h1: {
      fontSize: 32,
      lineHeight: 41.6,
      fontFamily: `IBMPlexSans-Bold${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    h2: {
      fontFamily: `IBMPlexSans-Bold${Platform.OS === "web" ? ",sans-serif" : ""}`,
      fontSize: 24,
      lineHeight: 31.2,
    },
    h3: {
      fontWeight: "700",
      fontSize: 24,
      lineHeight: 28,
    },
    h4: {
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
      fontSize: 22,
      lineHeight: 26,
    },
    p: {
      fontWeight: "400",
      fontSize: 18,
      lineHeight: 22,
    },
    label: {
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
      fontSize: 14,
      lineHeight: 17,
    },
    label2: {
      fontSize: 16,
      lineHeight: 19,
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    label3: {
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
      fontSize: 18,
      lineHeight: 22,
    },
    sectionTitle: {
      fontWeight: "400",
      fontSize: 16,
      lineHeight: 19,
    },
    primaryButton: {
      fontWeight: "600",
      fontSize: 18,
      lineHeight: 22,
    },
    secondaryButton: {
      fontWeight: "600",
      fontSize: 18,
      lineHeight: 22,
    },
    primaryButton3D: {
      fontSize: 18,
      lineHeight: 23.4,
      fontFamily: `IBMPlexSans-Medium${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    secondaryButton3D: {
      fontSize: 18,
      lineHeight: 23.4,
      fontFamily: `IBMPlexSans-Medium${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    body1: {
      fontSize: 18,
      lineHeight: 23.4,
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    body2: {
      fontSize: 16,
      lineHeight: 24,
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    body3: {
      fontSize: 16,
      lineHeight: 20.8,
      fontFamily: `IBMPlexSans-Regular${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
    unnamed: {
      fontSize: 14,
      lineHeight: 18,
      fontFamily: `IBMPlexSans-Medium${Platform.OS === "web" ? ",sans-serif" : ""}`,
    },
  }

  return styles
}

export function Text(props: TextProps) {
  const { type, color, underline, fontFamily, align, fontSize, lineHeight } = props
  const styles = useTextStyles()
  const theme = useTheme()

  let appliedStyles = {}
  if (type && styles[type]) {
    appliedStyles = {
      ...appliedStyles,
      ...styles[type],
      color: theme.text.primary,
    }
  }
  if (color) {
    appliedStyles = {
      ...appliedStyles,
      color: theme.text[color],
    }
  }
  if (underline) {
    appliedStyles = {
      ...appliedStyles,
      textDecorationLine: "underline",
    }
  }
  if (fontFamily) {
    appliedStyles = {
      ...appliedStyles,
      fontFamily: Platform.OS === "web" ? `${fontFamily},sans-serif` : fontFamily,
    }
  }
  if (align) {
    appliedStyles = {
      ...appliedStyles,
      textAlign: align,
    }
  }
  if (fontSize) {
    appliedStyles = {
      ...appliedStyles,
      fontSize,
    }
  }
  if (lineHeight) {
    appliedStyles = {
      ...appliedStyles,
      lineHeight,
    }
  }

  return <RNText maxFontSizeMultiplier={1.5} {...props} style={appliedStyles} />
}
